import { useRef, useEffect } from 'react'
import dayjs from 'dayjs'
import * as echarts from 'echarts/core'
import { BarChart, LineChart } from 'echarts/charts'
import {
  AxisPointerComponent,
  GridComponent,
  TooltipComponent,
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import Box from '@mui/material/Box'
import { useTheme, alpha } from '@mui/material/styles'
import { fmtStreamed } from './vocabulary'

echarts.use([
  BarChart,
  LineChart,
  AxisPointerComponent,
  GridComponent,
  TooltipComponent,
  CanvasRenderer,
])

// The 30 days on one card: audio streamed as a timeline on top, and under it
// each day's moments (pushed at the foot of the bar, the rest faded above),
// comments made and friends added as bars, the two panels on one day axis.
// Not one plot with two y-axes: hours and counts on one plot invite readings
// of where the line crosses a bar, which mean nothing. The panels share the
// axis and the hover instead: hovering a day lists every measure of it, and a
// click anywhere in its column opens it (the day view).

const PANEL_LEFT = 52
// Thin marks, the band's leftover left as air (the dataviz skill's bar spec).
const BAR_SPACING = { barMaxWidth: 10, barGap: '25%', barCategoryGap: '30%' }

const tooltipHtml = (row, timezone) => {
  const line = (value, label) =>
    `<div style="display:flex;gap:10px"><b style="min-width:52px">${value}</b><span style="opacity:.85">${label}</span></div>`
  return `<div style="min-width:190px">
    <div style="margin-bottom:4px">${dayjs(row.day).format(
      'ddd MMM D'
    )} · ${timezone}</div>
    ${line(fmtStreamed(row.streamed_ms), 'streamed')}
    ${line(
      row.quotes,
      `${row.quotes === 1 ? 'moment' : 'moments'} got · ${row.pushed} pushed`
    )}
    ${line(row.comments, `${row.comments === 1 ? 'comment' : 'comments'} made`)}
    ${line(
      row.friends_added,
      `${row.friends_added === 1 ? 'friend' : 'friends'} added`
    )}
    <div style="margin-top:4px;opacity:.6">Click for the day</div>
  </div>`
}

// The chart's option, a pure function of what it draws, so a test can render
// it without a canvas (`echarts.init(null, null, { ssr: true })`).
export const chartOption = ({ days, colors, selectedDay, timezone, theme }) => {
  const textColor = theme.palette.text.secondary
  const gridColor = alpha(textColor, 0.15)
  const surface = theme.palette.background.paper
  const labels = days.map((row) => row.day)
  // The picked day's column is shaded in both panels, so the day view below
  // is visibly about one column while every bar keeps its ink: a full-band
  // bar on axes of its own (hidden, one pair per panel), out of the grouped
  // bars' layout. A mark area cannot draw it: it snaps to whole categories.
  const band = (gridIndex) => ({
    name: 'picked day',
    type: 'bar',
    xAxisIndex: gridIndex + 2,
    yAxisIndex: gridIndex + 2,
    silent: true,
    z: 1,
    barWidth: '100%',
    itemStyle: { color: alpha(textColor, 0.12) },
    emphasis: { disabled: true },
    data: labels.map((day) => (day === selectedDay ? 1 : null)),
  })
  const hidden = { show: false }
  const bar = (value, color, top) => ({
    value,
    itemStyle: { color, borderRadius: top ? [4, 4, 0, 0] : 0 },
  })
  const axisLabel = { color: textColor, fontSize: 10 }
  return {
    animationDuration: 400,
    axisPointer: { link: [{ xAxisIndex: 'all' }] },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: 'rgba(33, 33, 41, 0.94)',
      borderWidth: 0,
      textStyle: { color: '#fff', fontSize: 12 },
      formatter: (params) => {
        const row = days[params[0]?.dataIndex]
        return row ? tooltipHtml(row, timezone) : ''
      },
    },
    grid: [
      { left: PANEL_LEFT, right: 12, top: 8, height: 56 },
      { left: PANEL_LEFT, right: 12, top: 84, bottom: 24 },
    ],
    xAxis: [
      ...[0, 1].map((gridIndex) => ({
        type: 'category',
        gridIndex,
        data: labels,
        axisTick: { show: false },
        axisLine: { lineStyle: { color: gridColor } },
        axisLabel:
          gridIndex === 0
            ? { show: false }
            : {
                ...axisLabel,
                formatter: (day) => dayjs(day).format('MMM D'),
              },
      })),
      // The bands' axes, after the drawn ones so their indices stay 0 and 1.
      ...[0, 1].map((gridIndex) => ({
        ...hidden,
        type: 'category',
        gridIndex,
        data: labels,
      })),
    ],
    yAxis: [
      {
        type: 'value',
        gridIndex: 0,
        splitNumber: 2,
        axisLabel: {
          ...axisLabel,
          formatter: (hours) => (hours ? `${hours} h` : '0'),
        },
        splitLine: { lineStyle: { color: gridColor } },
      },
      {
        type: 'value',
        gridIndex: 1,
        minInterval: 1,
        splitNumber: 3,
        axisLabel,
        splitLine: { lineStyle: { color: gridColor } },
      },
      ...[0, 1].map((gridIndex) => ({
        ...hidden,
        type: 'value',
        gridIndex,
        min: 0,
        max: 1,
      })),
    ],
    series: [
      band(0),
      band(1),
      {
        name: 'Streamed',
        type: 'line',
        xAxisIndex: 0,
        yAxisIndex: 0,
        color: colors.streamed,
        lineStyle: { width: 2, cap: 'round', join: 'round' },
        areaStyle: { color: alpha(colors.streamed, 0.1) },
        symbol: 'none',
        data: days.map((row) => ({
          value: Math.round((row.streamed_ms / 3_600_000) * 10) / 10,
          // The picked day carries the line's one marker.
          ...(row.day === selectedDay
            ? {
                symbol: 'circle',
                symbolSize: 8,
                itemStyle: { borderColor: surface, borderWidth: 2 },
              }
            : {}),
        })),
      },
      {
        name: 'Pushed',
        type: 'bar',
        ...BAR_SPACING,
        xAxisIndex: 1,
        yAxisIndex: 1,
        stack: 'moments',
        data: days.map((row) =>
          bar(row.pushed, colors.moments, row.pushed === row.quotes)
        ),
      },
      {
        name: 'Not pushed',
        type: 'bar',
        ...BAR_SPACING,
        xAxisIndex: 1,
        yAxisIndex: 1,
        stack: 'moments',
        data: days.map((row) =>
          bar(row.quotes - row.pushed, colors.unpushed, true)
        ),
      },
      {
        name: 'Comments made',
        type: 'bar',
        ...BAR_SPACING,
        xAxisIndex: 1,
        yAxisIndex: 1,
        data: days.map((row) => bar(row.comments, colors.comments, true)),
      },
      {
        name: 'Friends added',
        type: 'bar',
        ...BAR_SPACING,
        xAxisIndex: 1,
        yAxisIndex: 1,
        data: days.map((row) => bar(row.friends_added, colors.friends, true)),
      },
    ],
  }
}

const ActivityChart = ({
  days,
  colors,
  selectedDay,
  timezone = 'UTC',
  onSelectDay,
  height = 250,
}) => {
  const containerRef = useRef(null)
  const chartRef = useRef(null)
  const daysRef = useRef(days)
  daysRef.current = days
  const onSelectRef = useRef(onSelectDay)
  onSelectRef.current = onSelectDay
  const theme = useTheme()

  useEffect(() => {
    if (!containerRef.current) return undefined
    const chart = echarts.init(containerRef.current)
    chartRef.current = chart
    // The whole column is the target, not only a bar: a day with nothing in
    // it opens as well as one with a tall bar.
    chart.getZr().on('click', (event) => {
      const point = [event.offsetX, event.offsetY]
      const inside =
        chart.containPixel({ gridIndex: 0 }, point) ||
        chart.containPixel({ gridIndex: 1 }, point)
      if (!inside) return
      // The panels share their left and right, so either grid's x reads the
      // column; on a category axis it is the day's index.
      const [index] = chart.convertFromPixel({ gridIndex: 1 }, point)
      const row = daysRef.current[Math.round(index)]
      if (row) onSelectRef.current?.(row.day)
    })
    const observer = new ResizeObserver(() => chart.resize())
    observer.observe(containerRef.current)
    return () => {
      observer.disconnect()
      chart.dispose()
      chartRef.current = null
    }
  }, [])

  useEffect(() => {
    const chart = chartRef.current
    if (!chart) return
    chart.setOption(
      chartOption({ days, colors, selectedDay, timezone, theme }),
      {
        replaceMerge: ['series'],
      }
    )
  }, [days, colors, selectedDay, timezone, theme])

  return (
    // ECharts owns this node's children; React renders nothing into it.
    <Box ref={containerRef} sx={{ width: '100%', height, cursor: 'pointer' }} />
  )
}

export default ActivityChart
